import React, { useCallback, useRef, useState } from "react";

const useAudioPlayer = () => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [isMuted, setIsMuted] = useState(false);
  const [volume, setVolume] = useState(1);

  const musicPlayer = useRef<any>(null);
  const progressRef = useRef<any>(0);
  const volumeProgressRef = useRef<any>(1);
  const animationRef = useRef<any>(null);

  const onDurationChanged = (
    event: React.SyntheticEvent<HTMLMediaElement, Event>,
  ) => {
    const seconds = Math.floor(event.currentTarget.duration);
    setDuration(seconds);
    if (
      progressRef.current &&
      progressRef.current instanceof HTMLInputElement
    ) {
      progressRef.current.max = String(seconds);
    }
  };

  const onTimeUpdate = useCallback(() => {
    if (musicPlayer.current) {
      setCurrentTime(musicPlayer.current.currentTime);
      updateProgressBar(musicPlayer.current.currentTime);
    }
  }, []);

  const calculateTime = (secs: number) => {
    const minutes = Math.floor(secs / 60);
    const returnedMinutes = minutes < 10 ? `0${minutes}` : `${minutes}`;
    const seconds = Math.floor(secs % 60);
    const returnedSeconds = seconds < 10 ? `0${seconds}` : `${seconds}`;
    return `${returnedMinutes}:${returnedSeconds}`;
  };

  const togglePlayPause = (track: string, isAnotherTrack?: boolean) => {
    if (isPlaying) {
      togglePause();
      if (isAnotherTrack) {
        togglePlay(track, isAnotherTrack);
      }
    } else {
      togglePlay(track, isAnotherTrack);
    }
  };

  const togglePlay = (track: string, isAnotherTrack?: boolean) => {
    if (isAnotherTrack) {
      musicPlayer.current = new Audio(track);
      musicPlayer.current.currentTime = 0;
      setCurrentTime(0);
    }
    musicPlayer.current.play();
    setIsPlaying(true);
    animationRef.current = requestAnimationFrame(whilePlaying);
  };

  const togglePause = () => {
    musicPlayer.current.pause();
    setIsPlaying(false);
    cancelAnimationFrame(animationRef.current);
  };

  const whilePlaying = () => {
    if (musicPlayer.current) {
      if (progressRef.current?.value) {
        progressRef.current.value = musicPlayer.current.currentTime;
      }
    }
    changePlayerCurrentTime();
    updateProgressBar(musicPlayer.current.currentTime);
    animationRef.current = requestAnimationFrame(whilePlaying);
  };

  const updateProgressBar = (currentTime: number) => {
    if (progressRef.current) {
      const duration = Number(progressRef.current.max) || 1;
      const percent = (currentTime / duration) * 100;

      progressRef.current.style.background = `linear-gradient(to right, #ff5722 ${percent}%, #373737 ${percent}%)`;
    }
  };

  const changeRange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = Number(event.target.value);
    if (musicPlayer.current) {
      musicPlayer.current.currentTime = newValue;
    }

    const eventTargetMaxString = event.target.max;

    if (Number.isNaN(eventTargetMaxString)) {
      throw Error("event.target.max is NaN");
    }

    const eventTargetMax = Number(eventTargetMaxString);

    const percent = (newValue / eventTargetMax) * 100;
    event.target.style.background = `linear-gradient(to right, #ff5722 ${percent}%, #373737 ${percent}%)`;
  };

  const changePlayerCurrentTime = () => {
    progressRef.current?.style?.setProperty(
      "--seek-before-width",
      `${(progressRef.current.value / duration) * 100}%`,
    );
    setCurrentTime(progressRef.current?.value);
  };

  const toggleMute = () => {
    if (musicPlayer.current) {
      const muteStatus = !isMuted;
      musicPlayer.current.muted = muteStatus;
      setIsMuted(muteStatus);

      if (volumeProgressRef.current) {
        if (muteStatus) {
          volumeProgressRef.current?.style?.setProperty(
            "--volume-before-width",
            `${0}%`,
          );
          volumeProgressRef.current.style.background = `linear-gradient(to right, #373737 ${0}%, #373737 ${0}%)`; // Повністю вимкнений
        } else {
          volumeProgressRef.current?.style?.setProperty(
            "--volume-before-width",
            `${volumeProgressRef.current.value * 100}%`,
          );
          volumeProgressRef.current.style.background = `linear-gradient(to right, #ff5722 ${volume * 100}%, #373737 ${volume * 100}%)`;
        }
      }
    }
  };

  const changeVolume = (volumeLevel: number) => {
    if (musicPlayer.current) {
      const newVolume = Math.max(0, Math.min(volumeLevel, 1));
      musicPlayer.current.volume = newVolume;
      setVolume(newVolume);
      newVolume > 0 ? setIsMuted(false) : setIsMuted(true);

      if (volumeProgressRef.current) {
        volumeProgressRef.current?.style?.setProperty(
          "--volume-before-width",
          `${volumeProgressRef.current.value * 100}%`,
        );
        volumeProgressRef.current.style.background = `linear-gradient(to right, #ff5722 ${newVolume * 100}%, #373737 ${newVolume * 100}%)`;
      }
    }
  };

  return {
    isPlaying,
    setIsPlaying,
    duration,
    currentTime,
    musicPlayer,
    progressRef,
    onDurationChanged,
    onTimeUpdate,
    calculateTime,
    togglePlayPause,
    changeRange,
    toggleMute,
    changeVolume,
    isMuted,
    volume,
    volumeProgressRef,
  };
};

export default useAudioPlayer;
